import type { ConsoleAgentEvent, ConsoleAnalytics, ConsoleContact, ConsoleFunnel, ConsolePayment } from "./view-model";

export interface DashboardData {
  accountId: string;
  funnels: ConsoleFunnel[];
  contacts: ConsoleContact[];
  payments: ConsolePayment[];
  agentEvents: ConsoleAgentEvent[];
  analyticsByFunnel: Map<string, ConsoleAnalytics>;
}

const dashboardFunnelLimit = 20;
const dashboardLeadLimit = 100;

export async function loadDashboardData(fetcher: typeof fetch = fetch): Promise<DashboardData> {
  const funnelsResponse = await readJson<{
    accountId: string;
    funnels: ConsoleFunnel[];
  }>(`/dashboard/funnels?limit=${dashboardFunnelLimit}`, fetcher);
  const selectedFunnel = funnelsResponse.funnels[0];
  const [contactsResponse, paymentsResponse, agentResponse, firstAnalytics] = await Promise.all([
    readJson<{
    contacts: ConsoleContact[];
    }>(`/dashboard/contacts?limit=${dashboardLeadLimit}`, fetcher),
    readJson<{
    payments: ConsolePayment[];
    }>(`/dashboard/payments?limit=100`, fetcher),
    readJson<{
    events: ConsoleAgentEvent[];
    }>(`/dashboard/agent-events?limit=100`, fetcher),
    selectedFunnel ? loadFunnelAnalytics(selectedFunnel.funnelId, false, fetcher) : Promise.resolve(undefined)
  ]);
  const analyticsByFunnel = new Map<string, ConsoleAnalytics>();

  if (selectedFunnel && firstAnalytics) {
    analyticsByFunnel.set(analyticsCacheKey(selectedFunnel.funnelId, false), firstAnalytics);
  }

  return {
    accountId: funnelsResponse.accountId,
    funnels: funnelsResponse.funnels,
    contacts: contactsResponse.contacts,
    payments: paymentsResponse.payments,
    agentEvents: agentResponse.events,
    analyticsByFunnel
  };
}

export async function loadFunnelAnalytics(
  funnelId: string,
  includeSynthetic: boolean,
  fetcher: typeof fetch = fetch
): Promise<ConsoleAnalytics> {
  const analytics = await readJson<DashboardAnalyticsResponse>(
    `/dashboard/analytics?funnelId=${encodeURIComponent(funnelId)}${
      includeSynthetic ? "&includeSynthetic=true" : ""
    }`,
    fetcher
  );
  return stripDashboardAnalyticsEnvelope(analytics);
}

export function analyticsCacheKey(funnelId: string, includeSynthetic: boolean): string {
  return `${funnelId}:${includeSynthetic ? "synthetic" : "real"}`;
}

export function shouldLoadFunnelAnalytics(input: {
  analyticsByFunnel: Map<string, ConsoleAnalytics>;
  selectedFunnel: string;
  includeSynthetic: boolean;
  allowRequests: boolean;
}): boolean {
  if (!input.allowRequests || !input.selectedFunnel) {
    return false;
  }
  return !input.analyticsByFunnel.has(analyticsCacheKey(input.selectedFunnel, input.includeSynthetic));
}

type DashboardAnalyticsResponse = ConsoleAnalytics & { ok?: true };

function stripDashboardAnalyticsEnvelope(response: DashboardAnalyticsResponse): ConsoleAnalytics {
  const report = { ...response };
  delete report.ok;
  return report;
}

async function readJson<T>(path: string, fetcher: typeof fetch): Promise<T> {
  const response = await fetcher(path, {
    headers: {
      accept: "application/json"
    },
    credentials: "include"
  });
  if (!response.ok) {
    throw new Error(`Dashboard request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}
